import clsx from "clsx";
import { RegisterOptions, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import Toggle from "@/app/components/ui/Toggles";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import Input from "@/app/components/ui/Inputs/Input";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { IParavetStaffCreateBody } from "@/api_framework/api_modals/Configuration";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useConfigurationParavetStaffCreateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationParavetStaffCreateAPI";
import { useConfigurationParavetStaffDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationParavetStaffDetailAPI";

type ParavetStatus = "active" | "inactive";

const ParavetOnboardComponent = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { execute } = useConfigurationParavetStaffCreateAPI();
  const { execute: fetchDetail } = useConfigurationParavetStaffDetailAPI();
  const [status, setStatus] = useState<ParavetStatus>("active");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editParavetId = params.get("paravet_id") || undefined;
  const { cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const fetchCities = useOrgCitiesListAPI("INVENTORY");
  const { forEmail, forOnlyNumbers, forOnlyAlphabetWithoutDotSpace } = useFormValidations();
  const cityOrgItem = useSelectMenuReducer(cities, "name", "id");

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IParavetStaffCreateBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      phone_number: "",
      email: "",
      status: "active",
      employee_id: "",
      city_id: "",
      city_name: "",
      paravet_id: editParavetId,
    },
  });

  useEffect(() => {
    if (!editParavetId) return;
    let active = true;
    fetchDetail(editParavetId).then((data) => {
      if (!active) return;
      if (!data) return;
      setValue("name", data.name || "");
      setValue("phone_number", data.phone_number || "");
      setValue("email", data.email || "");
      setValue("employee_id", data.employee_id || "");
      setValue("city_id", data.city_id || "", { shouldValidate: true });
      setValue("city_name", data.city_name || "", { shouldValidate: true });
      setStatus((data.status?.toLowerCase() as ParavetStatus) || "active");
    });
    return () => {
      active = false;
    };
  }, [editParavetId, fetchDetail, setValue]);

  const onSubmit = async (data: IParavetStaffCreateBody) => {
    setIsSubmitting(true);
    try {
      await execute({
        ...data,
        status,
        city_id: data.city_id,
        paravet_id: editParavetId,
      });
      navigate(getRouteKey("PARAVET_SETUP", "url"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-medium text-[#000000]">Paravet Profile</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">Status</span>
          <Toggle
            checked={status === "active"}
            onToggle={() => setStatus((prev) => (prev === "active" ? "inactive" : "active"))}
          />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-100 bg-white mt-10 shadow-sm">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-2xl bg-slate-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-medium text-[#000000]">
                Personal Details <span className="text-rose-500">*</span>
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Paravet Name</label>
                <Input
                  className="text-xs"
                  placeholder="Enter paravet name"
                  register={register("name", {
                    ...((forOnlyAlphabetWithoutDotSpace?.validations as RegisterOptions<
                      IParavetStaffCreateBody,
                      "name"
                    >) || {}),
                    required: true,
                  })}
                  fieldError={errors.name}
                  errorMessages={[
                    forOnlyAlphabetWithoutDotSpace.errors,
                    { message: errors.name?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                  inputClassName="text-sm"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Phone Number</label>
                <Input
                  className="text-xs"
                  placeholder="1234 1234 1234"
                  maxLength={10}
                  inputClassName="text-sm"
                  register={register("phone_number", {
                    ...((forOnlyNumbers?.validations as RegisterOptions<IParavetStaffCreateBody, "phone_number">) ||
                      {}),
                    required: true,
                  })}
                  fieldError={errors.phone_number}
                  errorMessages={[
                    forOnlyNumbers.errors,
                    { message: errors.phone_number?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">Email</label>
                <Input
                  className="text-xs"
                  placeholder="name@example.com"
                  inputClassName="text-sm"
                  register={register("email", {
                    ...((forEmail?.validations as RegisterOptions<IParavetStaffCreateBody, "email">) || {}),
                    required: false,
                  })}
                  fieldError={errors.email}
                  errorMessages={[
                    forEmail.errors,
                    { message: errors.email?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">City</label>
                <NonSearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      setValue("city_id", item.id, { shouldValidate: true });
                      setValue("city_name", item.title || "", { shouldValidate: true });
                    }
                  }}
                  fieldError={errors?.city_id || null}
                  register={register("city_id", { required: true })}
                  selectItems={cityOrgItem}
                  placeholder="Select City"
                  showTooltips={false}
                  showDropdownIcon={true}
                  defaultSelected={cityOrgItem.find((c) => c.id === watch("city_id"))}
                  listBoxClassName="w-full"
                  className={clsx("text-gray-800")}
                  containerClassName="w-full"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#000000]">
                  Employee id <span className="text-rose-500">*</span>
                </label>
                <Input
                  className="text-xs"
                  placeholder="Enter Employee ID"
                  inputClassName="text-sm"
                  register={register("employee_id", {
                    required: true,
                  })}
                  fieldError={errors.employee_id}
                  errorMessages={[
                    { message: errors.employee_id?.message, type: "manual" },
                    { message: "", type: "required" },
                  ]}
                />
                <p className="text-[11px] text-[#6C727F]">
                  Must match the attendance/Truein employee code, otherwise the paravet cannot be matched to
                  attendance.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-3 mr-5 mb-3">
            <button
              type="button"
              onClick={() => navigate(getRouteKey("PARAVET_SETUP", "url"))}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={clsx(
                "rounded-lg px-6 py-2 text-sm font-medium text-white shadow-sm",
                isSubmitting ? "bg-slate-400 cursor-not-allowed" : "bg-black hover:bg-slate-900",
              )}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ParavetOnboardComponent;
